import { useState, useEffect, useCallback } from 'react'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Spinner,
} from '@/components/ui'
import { Button } from '@/components/ui'
import { Input } from '@/components/ui'
import { Badge } from '@/components/ui'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui'
import { Switch } from '@/components/ui'
import { useAppStore, useSettingsStore } from '@/stores'
import { useFeedbackStore } from '@/stores/feedbackStore'
import { apiClient } from '@/services/apiClient'
import { useEdgeLocalConfig } from '@/hooks/useEdgeLocalConfig'
import { getRagConnector } from '@/services/ragConnector'
import { elevenLabsProvider } from '@/connectors/voice/ElevenLabsProvider'
import { azureSpeechProvider } from '@/connectors/voice/AzureSpeechProvider'
import { tavusProvider } from '@/connectors/avatar/TavusProvider'
import { useToast } from '@/components/ui/Toast'
import {
  Server,
  Key,
  Mic,
  Video,
  Database,
  RefreshCw,
  Check,
  X,
  Eye,
  EyeOff,
  Save,
  Trash2,
  Volume2,
  Zap,
  Globe,
  ThumbsUp,
  ThumbsDown,
  Radio,
  Shield,
  Phone,
  Link2,
  Copy,
} from 'lucide-react'
import { useInbox } from '@/hooks/useInbox'
import { useFeature } from '@/hooks/useFeature'
import { STTLanguagePicker } from '@/components/voice/STTLanguagePicker'
import { useUserCredentialSchemas } from '@/hooks/useCredentialSchemas'
import { ApiKeyCard } from '@/components/settings/ApiKeyCard'
import { credentialErrorText } from '@/components/settings/credentialErrorText'
import type { LinkedAccount } from '@/types/inbox'
import type { CredentialServiceSchema } from '@/types/credentialSchemas'


// Backend storage stats type
interface StorageStats {
  personas_count: number
  conversations_count: number
  messages_count: number
  feedback_count: number
  positive_feedback_count: number
  negative_feedback_count: number
}
import { OUTPUT_FORMAT_LABELS } from '@/services/ElevenLabsStreamingService'
import type { StreamingOutputFormat } from '@/stores/settingsStore'
import {
  LOCAL_MAX_BUFFER_MS_STEPS,
  LOCAL_SILENCE_MS_STEPS,
  LOCAL_SILENCE_THRESHOLD_STEPS,
  OPEN_MIC_NO_WORDS_SECONDS,
  readLocalMaxBufferMs,
  readLocalSilenceMs,
  readLocalSilenceThreshold,
  readOpenMicNoWordsSeconds,
} from '@/types/voice'

/** Edge-local Advanced settings (SA-1393): option words */
const secondsText = (ms: number) => (ms === 1000 ? '1 second' : `${ms / 1000} seconds`)
const LOUDNESS_TEXT: Record<number, string> = {
  50: '−56 dB (most sensitive)',
  100: '−50 dB (more sensitive)',
  200: '−44 dB',
  400: '−38 dB (less sensitive)',
  800: '−32 dB (least sensitive)',
}
const selectValue = (value: number | null) => (value === null ? '' : String(value))
const storedValue = (value: string) => (value === '' ? null : Number(value))

/**
 * Credential service names matching the backend.
 */
const CREDENTIAL_SERVICES = {
  ELEVENLABS: 'elevenlabs',
  TAVUS: 'tavus',
  ANAM: 'anam',
  AZURE_SPEECH: 'azure-speech',
  RAG: 'rag',
  DEEPGRAM: 'deepgram',
  OPENAI: 'openai',
  STRIPE: 'stripe',
} as const

/** Services whose key editor lives on the Connections tab, not api-keys. */
const CONNECTIONS_TAB_SERVICES = new Set(['rag'])

/** Frontend-only presentation per service; the LIST comes from the registry
 *  (useUserCredentialSchemas). A service missing here still renders. */
const API_KEY_CARD_UI: Record<string, { description: string; order: number; featureGate?: 'commerce' }> = {
  elevenlabs: { description: 'Required for voice synthesis and conversational AI', order: 0 },
  deepgram: { description: 'Required for Pipeline voice mode (speech-to-text via Deepgram Nova)', order: 1 },
  openai: { description: 'Required for OpenAI Realtime voice mode (WebRTC-based speech-to-speech)', order: 2 },
  tavus: { description: 'Required for video avatar functionality', order: 3 },
  anam: { description: 'Required for Anam video avatar functionality', order: 4 },
  stripe: { description: 'Required for payment processing (orders, invoices)', order: 5, featureGate: 'commerce' },
  'azure-speech': { description: 'Optional: Alternative voice provider', order: 6 },
}

/** setCredentialStatus store keys that differ from the registry slug. */
const STORE_STATUS_KEY: Record<string, 'azure'> = { 'azure-speech': 'azure' }

export function SettingsPage() {
  const showVoice = useFeature('voice')
  const showInbox = useFeature('inbox')
  const showMarketing = useFeature('marketing')
  const showCommerce = useFeature('commerce')
  const { ragConnectionStatus, setRagConnectionStatus } = useAppStore()
  const {
    connections,
    voiceFeatures,
    voiceConversation: voiceConversationSettings,
    hasElevenLabsKey,
    hasTavusKey,
    hasAnamKey,
    hasAzureKey,
    hasRagApiKey,
    hasDeepgramKey,
    hasOpenAIKey,
    hasStripeKey,
    setRagApiKey,
    clearRagApiKey,
    setVoiceProvider,
    setAvatarProvider,
    setCredentialStatus,
    cacheCredential,
    cacheAzureCredential,
    clearCredentialCache,
    setUseStreamingTts,
    setStreamingConfig,
    setVoiceConversation,
    credentialMeta,
  } = useSettingsStore()

  const { isEdgeLocal } = useEdgeLocalConfig()
  const { addToast } = useToast()

  // Helper: check if a credential is managed by an admin or inherited from a set
  const isCredentialManaged = (service: string): boolean => {
    const meta = credentialMeta[service]
    if (!meta) return false
    return meta.isAdminManaged || meta.source === 'credential_set'
  }

  // Helper: get a human-readable label for the credential source
  const getCredentialSourceLabel = (service: string): string | null => {
    const meta = credentialMeta[service]
    if (!meta) return null
    if (meta.source === 'credential_set') {
      return meta.credentialSetName
        ? `Provided via '${meta.credentialSetName}' credential set`
        : 'Provided via credential set'
    }
    if (meta.isAdminManaged) {
      return meta.setByName
        ? `Set by ${meta.setByName}`
        : 'Set by your administrator'
    }
    return null
  }

  // Helper: render a managed-credential banner for read-only display
  const renderManagedBanner = (service: string) => (
    <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
      <Shield className="w-5 h-5 text-primary shrink-0" />
      <div>
        <p className="text-sm font-medium text-foreground">
          {getCredentialSourceLabel(service)}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Contact your administrator to change this credential.
        </p>
      </div>
    </div>
  )

  // Helper: render a status badge for credential source
  const renderCredentialBadge = (service: string, hasKey: boolean) => {
    if (!hasKey) return null
    const meta = credentialMeta[service]
    if (meta?.source === 'credential_set') {
      return (
        <Badge variant="default">
          <Shield className="w-3 h-3 mr-1" />
          Via Set
        </Badge>
      )
    }
    if (meta?.isAdminManaged) {
      return (
        <Badge variant="default">
          <Shield className="w-3 h-3 mr-1" />
          Admin
        </Badge>
      )
    }
    return (
      <Badge variant="success">
        <Check className="w-3 h-3 mr-1" />
        Configured
      </Badge>
    )
  }

  // Feedback store for local stats
  const { stats: localFeedbackStats, pendingSyncCount, clearAll: clearLocalFeedback } = useFeedbackStore()

  // Local state for form inputs
  const [ragApiKeyInput, setRagApiKeyInput] = useState('')

  const [showApiKeys, setShowApiKeys] = useState<Record<string, boolean>>({})

  // Saving state for async credential operations
  const [savingCredential, setSavingCredential] = useState<string | null>(null)

  // Backend storage stats
  const [storageStats, setStorageStats] = useState<StorageStats | null>(null)
  const [isLoadingStats, setIsLoadingStats] = useState(false)

  // Meta Marketing account connection
  const [metaTokenInput, setMetaTokenInput] = useState('')
  const [metaAdAccountInput, setMetaAdAccountInput] = useState('')
  const [metaPixelInput, setMetaPixelInput] = useState('')
  const [metaPageInput, setMetaPageInput] = useState('')
  const [metaAccount, setMetaAccount] = useState<{ name: string; adAccountId: string; isActive: boolean } | null>(null)
  const [isConnectingMeta, setIsConnectingMeta] = useState(false)
  const [isLoadingMeta, setIsLoadingMeta] = useState(false)

  // Linked accounts
  const { generateLinkCode, fetchLinkedAccounts, unlinkAccount } = useInbox()
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([])
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)
  const [linkCode, setLinkCode] = useState<string | null>(null)
  const [linkCodeExpiry, setLinkCodeExpiry] = useState<string | null>(null)
  const [isGeneratingCode, setIsGeneratingCode] = useState(false)
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null)

  // Fetch backend storage stats
  const fetchStorageStats = useCallback(async () => {
    setIsLoadingStats(true)
    try {
      const response = await apiClient.fetch(`/api/stats/storage`)
      if (response.ok) {
        const data = await response.json()
        setStorageStats(data)
      }
    } catch (error) {
      console.warn('Failed to fetch storage stats:', error)
    } finally {
      setIsLoadingStats(false)
    }
  }, [])

  // Fetch linked accounts
  const loadLinkedAccounts = useCallback(async () => {
    setIsLoadingAccounts(true)
    try {
      const accounts = await fetchLinkedAccounts()
      setLinkedAccounts(accounts)
    } catch {
      // silently ignore — feature may not be available
    } finally {
      setIsLoadingAccounts(false)
    }
  }, [fetchLinkedAccounts])

  const handleGenerateLinkCode = useCallback(async () => {
    setIsGeneratingCode(true)
    try {
      const result = await generateLinkCode()
      if (result) {
        setLinkCode(result.code)
        setLinkCodeExpiry(result.expiresAt)
        addToast({
          title: 'Link code generated',
          description: `Send this code via WhatsApp to link your account. Expires in 10 minutes.`,
          type: 'success',
        })
      }
    } catch {
      addToast({
        title: 'Failed to generate link code',
        description: 'Please try again.',
        type: 'error',
      })
    } finally {
      setIsGeneratingCode(false)
    }
  }, [generateLinkCode, addToast])

  const handleUnlinkAccount = useCallback(async (id: string) => {
    setUnlinkingId(id)
    try {
      await unlinkAccount(id)
      setLinkedAccounts((prev) => prev.filter((a) => a.id !== id))
      addToast({
        title: 'Account unlinked',
        description: 'The external account has been disconnected.',
        type: 'success',
      })
    } catch {
      addToast({
        title: 'Failed to unlink account',
        description: 'Please try again.',
        type: 'error',
      })
    } finally {
      setUnlinkingId(null)
    }
  }, [unlinkAccount, addToast])

  const copyLinkCode = useCallback(() => {
    if (linkCode) {
      navigator.clipboard.writeText(linkCode)
      addToast({ title: 'Copied', description: 'Link code copied to clipboard.', type: 'success' })
    }
  }, [linkCode, addToast])

  // Meta Marketing account handlers
  const loadMetaAccount = useCallback(async () => {
    setIsLoadingMeta(true)
    try {
      const response = await apiClient.fetch('/api/marketing/account')
      if (response.ok) {
        const data = await response.json()
        if (data) {
          setMetaAccount({ name: data.name || data.ad_account_id, adAccountId: data.ad_account_id, isActive: data.is_active })
        }
      }
    } catch {
      // Marketing feature may not be enabled
    } finally {
      setIsLoadingMeta(false)
    }
  }, [])

  const connectMetaAccount = useCallback(async () => {
    const token = metaTokenInput.trim()
    const adAccountId = metaAdAccountInput.trim()
    if (!token || !adAccountId) return
    setIsConnectingMeta(true)
    try {
      const response = await apiClient.fetch('/api/marketing/account', {
        method: 'POST',
        body: JSON.stringify({
          access_token: token,
          ad_account_id: adAccountId,
          pixel_id: metaPixelInput.trim() || null,
          page_id: metaPageInput.trim() || null,
        }),
      })
      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: 'Connection failed' }))
        throw new Error(err.detail || 'Connection failed')
      }
      const data = await response.json()
      setMetaAccount({ name: data.name || data.ad_account_id, adAccountId: data.ad_account_id, isActive: true })
      setMetaTokenInput('')
      setMetaAdAccountInput('')
      setMetaPixelInput('')
      setMetaPageInput('')
      addToast({ title: 'Meta Ad Account connected', description: `Connected: ${data.name || data.ad_account_id}`, type: 'success' })
    } catch (error) {
      addToast({ title: 'Connection failed', description: error instanceof Error ? error.message : 'Unknown error', type: 'error' })
    } finally {
      setIsConnectingMeta(false)
    }
  }, [metaTokenInput, metaAdAccountInput, metaPixelInput, metaPageInput, addToast])

  const disconnectMetaAccount = useCallback(async () => {
    try {
      await apiClient.fetch('/api/marketing/account', { method: 'DELETE' })
      setMetaAccount(null)
      addToast({ title: 'Disconnected', description: 'Meta Ad Account has been disconnected.', type: 'success' })
    } catch {
      addToast({ title: 'Failed to disconnect', description: 'Please try again.', type: 'error' })
    }
  }, [addToast])

  // Fetch storage stats, linked accounts, and marketing account on mount
  useEffect(() => {
    fetchStorageStats()
    loadLinkedAccounts()
    if (showMarketing) loadMetaAccount()
  }, [fetchStorageStats, loadLinkedAccounts, showMarketing, loadMetaAccount])

  const toggleApiKeyVisibility = (key: string) => {
    setShowApiKeys((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  // --- Credential save/clear helpers (backend API) ---

  const saveCredentialToBackend = async (
    service: string,
    name: string,
    apiKey: string,
    config?: Record<string, unknown>,
  ) => {
    const response = await apiClient.fetch('/api/credentials', {
      method: 'POST',
      body: JSON.stringify({
        service,
        name,
        api_key: apiKey,
        config: config || null,
      }),
    })
    if (!response.ok) {
      const body = await response.json().catch(() => null)
      throw new Error(credentialErrorText(body, 'Failed to save credential'))
    }
    return response.json()
  }

  const deleteCredentialFromBackend = async (service: string) => {
    const response = await apiClient.fetch(`/api/credentials/${service}`, {
      method: 'DELETE',
    })
    if (!response.ok && response.status !== 404) {
      const body = await response.json().catch(() => null)
      throw new Error(credentialErrorText(body, 'Failed to delete credential'))
    }
  }

  // --- Save handlers ---

  // saveServerUrl removed in Phase 5 — RAG URL is now deployment-level.

  const saveRagApiKey = async () => {
    const key = ragApiKeyInput.trim()
    if (!key) return
    setSavingCredential('rag')
    try {
      await saveCredentialToBackend(CREDENTIAL_SERVICES.RAG, 'RAG Server API Key', key)
      setRagApiKey(key)
      setCredentialStatus('rag', true)
      getRagConnector().setApiKey(key)
      setRagApiKeyInput('')
      addToast({
        title: 'API key saved',
        description: 'RAG server API key has been saved to the backend.',
        type: 'success',
      })
    } catch (error) {
      addToast({
        title: 'Failed to save API key',
        description: error instanceof Error ? error.message : 'Unknown error',
        type: 'error',
      })
    } finally {
      setSavingCredential(null)
    }
  }

  const clearRagApiKeyHandler = async () => {
    setSavingCredential('rag-clear')
    try {
      await deleteCredentialFromBackend(CREDENTIAL_SERVICES.RAG)
      clearRagApiKey()
      setCredentialStatus('rag', false)
      getRagConnector().setApiKey('')
      addToast({
        title: 'API key cleared',
        description: 'RAG server API key has been removed.',
        type: 'success',
      })
    } catch (error) {
      addToast({
        title: 'Failed to clear API key',
        description: error instanceof Error ? error.message : 'Unknown error',
        type: 'error',
      })
    } finally {
      setSavingCredential(null)
    }
  }

  const testConnection = async () => {
    setRagConnectionStatus('connecting')
    // Test against the deployment-level base URL (already wired via useAppInit).
    getRagConnector().setApiKey(connections.ragApiKey)

    const result = await getRagConnector().testConnection()

    if (result.success) {
      setRagConnectionStatus('connected')
      addToast({
        title: 'Connection successful',
        description: 'Successfully connected to the RAG server.',
        type: 'success',
      })
    } else {
      setRagConnectionStatus('error')
      addToast({
        title: 'Connection failed',
        description: result.error || 'Failed to connect to the RAG server.',
        type: 'error',
      })
    }
  }

  const handleClearAllCredentials = async () => {
    setSavingCredential('all-clear')
    try {
      // Only delete user-managed (non-admin, non-set) credentials
      const allServices = [
        CREDENTIAL_SERVICES.ELEVENLABS,
        CREDENTIAL_SERVICES.TAVUS,
        CREDENTIAL_SERVICES.ANAM,
        CREDENTIAL_SERVICES.AZURE_SPEECH,
        CREDENTIAL_SERVICES.RAG,
        CREDENTIAL_SERVICES.DEEPGRAM,
        CREDENTIAL_SERVICES.OPENAI,
        CREDENTIAL_SERVICES.STRIPE,
      ]
      const userManagedServices = allServices.filter((svc) => !isCredentialManaged(svc))

      if (userManagedServices.length === 0) {
        addToast({
          title: 'No credentials to clear',
          description: 'All your credentials are managed by an administrator.',
          type: 'info',
        })
        return
      }

      await Promise.allSettled(
        userManagedServices.map((svc) => deleteCredentialFromBackend(svc)),
      )

      // Only clear status for user-managed credentials
      for (const svc of userManagedServices) {
        if (svc === CREDENTIAL_SERVICES.ELEVENLABS) setCredentialStatus('elevenlabs', false)
        else if (svc === CREDENTIAL_SERVICES.TAVUS) setCredentialStatus('tavus', false)
        else if (svc === CREDENTIAL_SERVICES.ANAM) setCredentialStatus('anam', false)
        else if (svc === CREDENTIAL_SERVICES.AZURE_SPEECH) setCredentialStatus('azure', false)
        else if (svc === CREDENTIAL_SERVICES.RAG) {
          setCredentialStatus('rag', false)
          clearRagApiKey()
          getRagConnector().setApiKey('')
        }
        else if (svc === CREDENTIAL_SERVICES.DEEPGRAM) setCredentialStatus('deepgram', false)
        else if (svc === CREDENTIAL_SERVICES.OPENAI) setCredentialStatus('openai', false)
        else if (svc === CREDENTIAL_SERVICES.STRIPE) setCredentialStatus('stripe', false)
      }
      clearCredentialCache()

      addToast({
        title: 'Credentials cleared',
        description: 'Your user-managed API credentials have been removed.',
        type: 'success',
      })
    } catch (error) {
      addToast({
        title: 'Failed to clear credentials',
        description: error instanceof Error ? error.message : 'Unknown error',
        type: 'error',
      })
    } finally {
      setSavingCredential(null)
    }
  }

  // --- Schema-driven api-keys tab (§4.5) ---

  const {
    data: userSchemas = [],
    isLoading: schemasLoading,
    error: schemasError,
    refetch: refetchSchemas,
  } = useUserCredentialSchemas()

  const hasKeyByService: Record<string, boolean> = {
    elevenlabs: hasElevenLabsKey,
    deepgram: hasDeepgramKey,
    openai: hasOpenAIKey,
    tavus: hasTavusKey,
    anam: hasAnamKey,
    stripe: hasStripeKey,
    'azure-speech': hasAzureKey,
    rag: hasRagApiKey,
  }

  const apiKeyCardSchemas = userSchemas
    .filter((s) => !CONNECTIONS_TAB_SERVICES.has(s.service))
    .filter((s) => {
      const gate = API_KEY_CARD_UI[s.service]?.featureGate
      if (gate === 'commerce') return showCommerce
      return true
    })
    .sort((a, b) => {
      const ao = API_KEY_CARD_UI[a.service]?.order ?? 100
      const bo = API_KEY_CARD_UI[b.service]?.order ?? 100
      return ao !== bo ? ao - bo : a.service.localeCompare(b.service)
    })

  // Post-save side effects — the only per-service code left on this tab.
  const apiKeySideEffects: Record<string, {
    afterSave?: (key: string, config: Record<string, string>) => Promise<void> | void
  }> = {
    elevenlabs: {
      afterSave: async (key) => {
        cacheCredential('elevenlabs', key)
        await elevenLabsProvider.initialize(key)
      },
    },
    tavus: {
      afterSave: async (key) => {
        cacheCredential('tavus', key)
        await tavusProvider.initialize(key)
      },
    },
    'azure-speech': {
      afterSave: async (key, config) => {
        const region = config.region ?? ''
        cacheAzureCredential(key, region)
        await azureSpeechProvider.initialize(`${key}:${region}`)
      },
    },
  }

  const handleSaveApiKey = async (schema: CredentialServiceSchema, key: string, config: Record<string, string>) => {
    if (!key) return
    setSavingCredential(schema.service)
    try {
      await saveCredentialToBackend(
        schema.service,
        `${schema.label} API Key`,
        key,
        Object.keys(config).length > 0 ? config : undefined,
      )
      // STORE_STATUS_KEY maps registry slugs to the store's narrower service
      // union (e.g. 'azure-speech' -> 'azure'); cast is safe because every
      // registry slug we render here has a corresponding store key.
      setCredentialStatus(
        (STORE_STATUS_KEY[schema.service] ?? schema.service) as Parameters<typeof setCredentialStatus>[0],
        true,
      )
      await apiKeySideEffects[schema.service]?.afterSave?.(key, config)
      addToast({
        title: 'API key saved',
        description: `${schema.label} API key has been saved to the backend.`,
        type: 'success',
      })
    } catch (error) {
      addToast({
        title: 'Failed to save API key',
        description: error instanceof Error ? error.message : 'Unknown error',
        type: 'error',
      })
      // Rethrow so ApiKeyCard's handleSave sees the rejection and preserves
      // the typed key/config instead of clearing them on a failed save.
      throw error
    } finally {
      setSavingCredential(null)
    }
  }

  const handleClearApiKey = async (schema: CredentialServiceSchema) => {
    setSavingCredential(`${schema.service}-clear`)
    try {
      await deleteCredentialFromBackend(schema.service)
      setCredentialStatus(
        (STORE_STATUS_KEY[schema.service] ?? schema.service) as Parameters<typeof setCredentialStatus>[0],
        false,
      )
      addToast({
        title: 'API key cleared',
        description: `${schema.label} API key has been removed.`,
        type: 'success',
      })
    } catch (error) {
      addToast({
        title: 'Failed to clear API key',
        description: error instanceof Error ? error.message : 'Unknown error',
        type: 'error',
      })
    } finally {
      setSavingCredential(null)
    }
  }

  return (
    <PageFrame width="form" className="space-y-6">
      <PageHeader title="Settings" subtitle="Configure API connections and application settings" />

      <Tabs defaultValue="connections">
        <TabsList>
          <TabsTrigger value="connections">
            <Server className="w-4 h-4 mr-2" />
            Connections
          </TabsTrigger>
          {showVoice && (
            <TabsTrigger value="voice">
              <Volume2 className="w-4 h-4 mr-2" />
              Voice
            </TabsTrigger>
          )}
          <TabsTrigger value="api-keys">
            <Key className="w-4 h-4 mr-2" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="storage">
            <Database className="w-4 h-4 mr-2" />
            Storage
          </TabsTrigger>
        </TabsList>

        {/* Connections Tab */}
        <TabsContent value="connections" className="space-y-4 mt-4">
          {/* RAG Server */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="w-5 h-5" />
                RAG Server Connection
              </CardTitle>
              <CardDescription>
                Connect to your ai-hybridRAG-server instance
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge
                  variant={
                    ragConnectionStatus === 'connected'
                      ? 'success'
                      : ragConnectionStatus === 'connecting'
                      ? 'warning'
                      : ragConnectionStatus === 'error'
                      ? 'destructive'
                      : 'secondary'
                  }
                >
                  {ragConnectionStatus === 'connected' && <Check className="w-3 h-3 mr-1" />}
                  {ragConnectionStatus === 'error' && <X className="w-3 h-3 mr-1" />}
                  {ragConnectionStatus}
                </Badge>
              </div>

              {/* Server URL is now deployment-level (RAG_PROXY_BASE env var,
                  exposed via /api/features). Test button preserved so admins
                  can verify the running deployment can reach the RAG. */}
              <div className="space-y-2">
                <Button
                  variant="secondary"
                  onClick={testConnection}
                  isLoading={ragConnectionStatus === 'connecting'}
                  leftIcon={<RefreshCw className="w-4 h-4" />}
                >
                  Test connection
                </Button>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  API Key
                  {renderCredentialBadge('rag', hasRagApiKey)}
                </label>
                {isCredentialManaged('rag') ? (
                  renderManagedBanner('rag')
                ) : (
                  <>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          type={showApiKeys['ragServer'] ? 'text' : 'password'}
                          placeholder={hasRagApiKey ? '••••••••••••••••' : 'Enter RAG server API key (optional)'}
                          value={ragApiKeyInput}
                          onChange={(e) => setRagApiKeyInput(e.target.value)}
                        />
                        <button
                          onClick={() => toggleApiKeyVisibility('ragServer')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showApiKeys['ragServer'] ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <Button
                        onClick={saveRagApiKey}
                        disabled={!ragApiKeyInput.trim() || savingCredential === 'rag'}
                        isLoading={savingCredential === 'rag'}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save
                      </Button>
                      {hasRagApiKey && (
                        <Button
                          variant="destructive"
                          onClick={clearRagApiKeyHandler}
                          disabled={savingCredential === 'rag-clear'}
                          isLoading={savingCredential === 'rag-clear'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Optional: API key sent via x-api-key header for authenticating with the RAG server
                    </p>
                  </>
                )}
              </div>

              <Alert>
                <AlertTitle>OpenAI-Compatible API</AlertTitle>
                <AlertDescription>
                  This app connects to the ai-hybridRAG-server via its OpenAI-compatible endpoints
                  at /v1/chat/completions.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Voice Provider */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mic className="w-5 h-5" />
                Voice Provider
              </CardTitle>
              <CardDescription>
                Configure voice synthesis and speech-to-text
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card
                  className={`cursor-pointer transition-colors ${
                    connections.voiceProvider === 'elevenlabs'
                      ? 'border-primary'
                      : 'hover:border-muted-foreground/40'
                  }`}
                  onClick={() => setVoiceProvider('elevenlabs')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">ElevenLabs</h4>
                        <p className="text-sm text-muted-foreground">Conversational AI 2.0</p>
                      </div>
                      {connections.voiceProvider === 'elevenlabs' ? (
                        <Badge variant="success">Selected</Badge>
                      ) : (
                        <Badge variant="secondary">Available</Badge>
                      )}
                    </div>
                    {hasElevenLabsKey && (
                      <p className="text-xs text-success mt-2">
                        ✓ API key configured
                      </p>
                    )}
                  </CardContent>
                </Card>
                <Card
                  className={`cursor-pointer transition-colors ${
                    connections.voiceProvider === 'azure'
                      ? 'border-primary'
                      : 'hover:border-muted-foreground/40'
                  }`}
                  onClick={() => setVoiceProvider('azure')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Azure Speech</h4>
                        <p className="text-sm text-muted-foreground">Speech Services</p>
                      </div>
                      {connections.voiceProvider === 'azure' ? (
                        <Badge variant="success">Selected</Badge>
                      ) : (
                        <Badge variant="secondary">Available</Badge>
                      )}
                    </div>
                    {hasAzureKey && (
                      <p className="text-xs text-success mt-2">
                        ✓ Credentials configured
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          {/* Avatar Provider */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Video className="w-5 h-5" />
                Avatar Provider
              </CardTitle>
              <CardDescription>
                Configure video avatar services
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card
                  className={`cursor-pointer transition-colors ${
                    connections.avatarProvider === 'tavus'
                      ? 'border-primary'
                      : 'hover:border-muted-foreground/40'
                  }`}
                  onClick={() => setAvatarProvider('tavus')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Tavus</h4>
                        <p className="text-sm text-muted-foreground">Conversational Video API</p>
                      </div>
                      {connections.avatarProvider === 'tavus' ? (
                        <Badge variant="success">Selected</Badge>
                      ) : (
                        <Badge variant="secondary">Available</Badge>
                      )}
                    </div>
                    {hasTavusKey && (
                      <p className="text-xs text-success mt-2">
                        ✓ API key configured
                      </p>
                    )}
                  </CardContent>
                </Card>
                <Card
                  className={`cursor-pointer transition-colors ${
                    connections.avatarProvider === 'anam'
                      ? 'border-primary'
                      : 'hover:border-muted-foreground/40'
                  }`}
                  onClick={() => setAvatarProvider('anam')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Anam</h4>
                        <p className="text-sm text-muted-foreground">AI Streaming Avatars</p>
                      </div>
                      {connections.avatarProvider === 'anam' ? (
                        <Badge variant="success">Selected</Badge>
                      ) : (
                        <Badge variant="secondary">Available</Badge>
                      )}
                    </div>
                    {hasAnamKey && (
                      <p className="text-xs text-success mt-2">
                        ✓ API key configured
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Voice Tab */}
        {showVoice && <TabsContent value="voice" className="space-y-4 mt-4">
          {/* Streaming TTS Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Low-Latency Streaming TTS
              </CardTitle>
              <CardDescription>
                Enable real-time voice streaming for faster response times. Audio starts playing
                as soon as text is generated, rather than waiting for the complete response.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <h4 className="font-medium">Enable Streaming TTS</h4>
                  <p className="text-sm text-muted-foreground">
                    Use WebSocket streaming for lowest latency (~75-300ms)
                  </p>
                </div>
                <Switch
                  checked={voiceFeatures.useStreamingTts}
                  onCheckedChange={setUseStreamingTts}
                  disabled={!hasElevenLabsKey}
                  aria-label="Enable Streaming TTS"
                />
              </div>

              {!hasElevenLabsKey && !isEdgeLocal && (
                <Alert>
                  <AlertTitle>ElevenLabs API Key Required</AlertTitle>
                  <AlertDescription>
                    Configure your ElevenLabs API key in the API Keys tab to enable streaming TTS.
                  </AlertDescription>
                </Alert>
              )}

              {isEdgeLocal && (
                <Alert>
                  <AlertTitle>Local TTS Active</AlertTitle>
                  <AlertDescription>
                    Text-to-speech uses the local Kokoro engine. Configure personas with the "Kokoro (Local)" voice provider.
                    No ElevenLabs key needed.
                  </AlertDescription>
                </Alert>
              )}

              {voiceFeatures.useStreamingTts && hasElevenLabsKey && (
                <>
                  {/* Global Endpoint Toggle */}
                  <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <h4 className="font-medium">Use Global Endpoint</h4>
                        <p className="text-sm text-muted-foreground">
                          Routes to geographically closest server for lower latency
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={voiceFeatures.streamingConfig.useGlobalEndpoint}
                      onCheckedChange={(checked) =>
                        setStreamingConfig({ useGlobalEndpoint: checked })
                      }
                      aria-label="Use Global Endpoint"
                    />
                  </div>

                  {/* Auto Mode Toggle */}
                  <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div className="flex items-center gap-3">
                      <Zap className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <h4 className="font-medium">Auto Mode (experimental)</h4>
                        <p className="text-sm text-muted-foreground">
                          Synthesize on stream pauses for lower latency. Can make
                          pronunciation dictionaries apply inconsistently.
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={voiceFeatures.streamingConfig.autoMode}
                      onCheckedChange={(checked) =>
                        setStreamingConfig({ autoMode: checked })
                      }
                      aria-label="Auto Mode"
                    />
                  </div>

                  {/* Output Format Selector */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      Default Audio Format
                    </label>
                    <select
                      value={voiceFeatures.streamingConfig.defaultOutputFormat}
                      onChange={(e) =>
                        setStreamingConfig({
                          defaultOutputFormat: e.target.value as StreamingOutputFormat,
                        })
                      }
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
                    >
                      {Object.entries(OUTPUT_FORMAT_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground">
                      PCM formats have lowest latency but larger data size. MP3 formats are
                      compressed but add some decoding overhead.
                    </p>
                  </div>

                  {/* Pre-buffer Duration */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      Pre-buffer Duration: {voiceFeatures.streamingConfig.preBufferMs}ms
                    </label>
                    <input
                      type="range"
                      min="50"
                      max="500"
                      step="25"
                      value={voiceFeatures.streamingConfig.preBufferMs}
                      onChange={(e) =>
                        setStreamingConfig({ preBufferMs: parseInt(e.target.value) })
                      }
                      className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                    />
                    <p className="text-xs text-muted-foreground">
                      Amount of audio to buffer before starting playback. Lower values reduce
                      latency but may cause choppy audio on slow connections.
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Voice Conversation Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Radio className="w-5 h-5" />
                Voice Conversation
              </CardTitle>
              <CardDescription>
                Full-duplex voice conversation. Choose Pipeline mode (Deepgram STT + RAG + ElevenLabs TTS)
                or OpenAI Realtime mode (single WebRTC session with built-in STT/LLM/TTS).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <h4 className="font-medium">Enable Voice Conversation</h4>
                  <p className="text-sm text-muted-foreground">
                    Show the voice conversation button in the chat input
                  </p>
                </div>
                <Switch
                  checked={voiceConversationSettings.enabled}
                  onCheckedChange={(checked) => setVoiceConversation({ enabled: checked })}
                  disabled={
                    isEdgeLocal
                      ? false
                      : voiceConversationSettings.engine === 'pipeline'
                        ? !hasDeepgramKey
                        : !hasOpenAIKey
                  }
                  aria-label="Enable Voice Conversation"
                />
              </div>

              {isEdgeLocal && (
                <Alert>
                  <AlertTitle>Edge-Local Mode</AlertTitle>
                  <AlertDescription>
                    Voice services run locally on this device. No cloud API keys needed.
                    Select "Kokoro (Local)" as the voice provider when configuring personas.
                  </AlertDescription>
                </Alert>
              )}

              {!isEdgeLocal && voiceConversationSettings.engine === 'pipeline' && !hasDeepgramKey && (
                <Alert>
                  <AlertTitle>Deepgram API Key Required</AlertTitle>
                  <AlertDescription>
                    Configure your Deepgram API key in the API Keys tab to enable Pipeline voice mode.
                  </AlertDescription>
                </Alert>
              )}

              {!isEdgeLocal && voiceConversationSettings.engine === 'openai-realtime' && !hasOpenAIKey && (
                <Alert>
                  <AlertTitle>OpenAI API Key Required</AlertTitle>
                  <AlertDescription>
                    Configure your OpenAI API key in the API Keys tab to enable Realtime voice mode.
                  </AlertDescription>
                </Alert>
              )}

              {/* Engine Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Voice Engine
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setVoiceConversation({ engine: 'pipeline' })}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      voiceConversationSettings.engine === 'pipeline'
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-muted-foreground/40'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Zap className="w-4 h-4" />
                      <span className="font-medium text-sm">Pipeline</span>
                      {hasDeepgramKey && (
                        <Badge variant="success" className="text-[10px] px-1.5 py-0">
                          <Check className="w-2.5 h-2.5 mr-0.5" />
                          Ready
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Deepgram STT + RAG + ElevenLabs TTS. Best for customizable pipelines.
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setVoiceConversation({ engine: 'openai-realtime' })}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      voiceConversationSettings.engine === 'openai-realtime'
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-muted-foreground/40'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Globe className="w-4 h-4" />
                      <span className="font-medium text-sm">OpenAI Realtime</span>
                      {hasOpenAIKey && (
                        <Badge variant="success" className="text-[10px] px-1.5 py-0">
                          <Check className="w-2.5 h-2.5 mr-0.5" />
                          Ready
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Single WebRTC session. Built-in STT, LLM, and TTS. Low latency.
                    </p>
                  </button>
                </div>
              </div>

              {voiceConversationSettings.enabled && (
                isEdgeLocal ||
                (voiceConversationSettings.engine === 'pipeline' ? hasDeepgramKey : hasOpenAIKey)
              ) && (
                <>
                  {/* Interaction Mode */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      Interaction Mode
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setVoiceConversation({ mode: 'push-to-talk' })}
                        className={`p-3 rounded-lg border text-left transition-colors ${
                          voiceConversationSettings.mode === 'push-to-talk'
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-muted-foreground/40'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Mic className="w-4 h-4" />
                          <span className="font-medium text-sm">Push to Talk</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Hold button to talk, release to send. Click to toggle.
                        </p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setVoiceConversation({ mode: 'open-mic' })}
                        className={`p-3 rounded-lg border text-left transition-colors ${
                          voiceConversationSettings.mode === 'open-mic'
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-muted-foreground/40'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Radio className="w-4 h-4" />
                          <span className="font-medium text-sm">Open Mic</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Hands-free. Auto-detects speech, auto-resumes after AI responds.
                        </p>
                      </button>
                    </div>
                    {voiceConversationSettings.mode === 'open-mic' &&
                      voiceConversationSettings.engine === 'pipeline' &&
                      !isEdgeLocal && (
                        <div className="space-y-2 pt-2">
                          <label
                            htmlFor="open-mic-no-words-seconds"
                            className="text-sm font-medium text-foreground"
                          >
                            Stop after a sound with no words
                          </label>
                          <select
                            id="open-mic-no-words-seconds"
                            value={readOpenMicNoWordsSeconds(voiceConversationSettings.openMicNoWordsSeconds)}
                            onChange={(e) =>
                              setVoiceConversation({ openMicNoWordsSeconds: Number(e.target.value) })
                            }
                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
                          >
                            {Array.from(
                              { length: OPEN_MIC_NO_WORDS_SECONDS.max - OPEN_MIC_NO_WORDS_SECONDS.min + 1 },
                              (_, i) => OPEN_MIC_NO_WORDS_SECONDS.min + i,
                            ).map((n) => (
                              <option key={n} value={n}>
                                {n} seconds
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-muted-foreground">
                            When open mic hears a sound but no words follow, like a cough or a door, it
                            stops listening after this long and waits for speech again.
                          </p>
                        </div>
                      )}
                  </div>

                  {/* Show Transcript Toggle */}
                  <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div>
                      <h4 className="font-medium">Show Live Transcript</h4>
                      <p className="text-sm text-muted-foreground">
                        Display real-time partial transcript while speaking
                      </p>
                    </div>
                    <Switch
                      checked={voiceConversationSettings.showTranscript}
                      onCheckedChange={(checked) => setVoiceConversation({ showTranscript: checked })}
                      aria-label="Show Live Transcript"
                    />
                  </div>

                  {/* Auto-send Toggle */}
                  <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div>
                      <h4 className="font-medium">Auto-send Transcript</h4>
                      <p className="text-sm text-muted-foreground">
                        Automatically send transcript when speech ends
                      </p>
                    </div>
                    <Switch
                      checked={voiceConversationSettings.autoSend}
                      onCheckedChange={(checked) => setVoiceConversation({ autoSend: checked })}
                      aria-label="Auto-send Transcript"
                    />
                  </div>

                  {/* Edge-local: the speech server's thresholds, per person (SA-1393) */}
                  {isEdgeLocal && voiceConversationSettings.engine === 'pipeline' && (
                    <details data-testid="edge-voice-advanced" className="p-4 bg-muted rounded-lg">
                      <summary className="cursor-pointer font-medium">Advanced settings</summary>
                      <div className="space-y-4 pt-3">
                        <p className="text-sm text-muted-foreground">
                          These change how this device's speech server listens to you. Server
                          default uses the device's own setting.
                        </p>
                        <div className="space-y-2">
                          <label htmlFor="edge-silence-ms" className="text-sm font-medium text-foreground">
                            Pause that ends your turn
                          </label>
                          <select
                            id="edge-silence-ms"
                            value={selectValue(readLocalSilenceMs(voiceConversationSettings.localSilenceMs))}
                            onChange={(e) => setVoiceConversation({ localSilenceMs: storedValue(e.target.value) })}
                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
                          >
                            <option value="">Server default</option>
                            {LOCAL_SILENCE_MS_STEPS.map((ms) => (
                              <option key={ms} value={ms}>
                                {secondsText(ms)}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-muted-foreground">
                            How long you stop talking before your turn ends in open mic, or before
                            what you've said so far appears while you hold the button. Choose longer
                            if you get cut off mid-sentence.
                          </p>
                        </div>
                        <div className="space-y-2">
                          <label htmlFor="edge-silence-threshold" className="text-sm font-medium text-foreground">
                            How loud counts as speech
                          </label>
                          <select
                            id="edge-silence-threshold"
                            value={selectValue(readLocalSilenceThreshold(voiceConversationSettings.localSilenceThreshold))}
                            onChange={(e) =>
                              setVoiceConversation({ localSilenceThreshold: storedValue(e.target.value) })
                            }
                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
                          >
                            <option value="">Server default</option>
                            {LOCAL_SILENCE_THRESHOLD_STEPS.map((rms) => (
                              <option key={rms} value={rms}>
                                {LOUDNESS_TEXT[rms]}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-muted-foreground">
                            Raise it if a fan or room noise keeps your turn open. Lower it if quiet
                            speech is missed.
                          </p>
                        </div>
                        <div className="space-y-2">
                          <label htmlFor="edge-max-buffer-ms" className="text-sm font-medium text-foreground">
                            Longest stretch before writing out
                          </label>
                          <select
                            id="edge-max-buffer-ms"
                            value={selectValue(readLocalMaxBufferMs(voiceConversationSettings.localMaxBufferMs))}
                            onChange={(e) => setVoiceConversation({ localMaxBufferMs: storedValue(e.target.value) })}
                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
                          >
                            <option value="">Server default</option>
                            {LOCAL_MAX_BUFFER_MS_STEPS.map((ms) => (
                              <option key={ms} value={ms}>
                                {secondsText(ms)}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-muted-foreground">
                            In long, unbroken speech, how much audio is gathered before it is turned
                            into text.
                          </p>
                        </div>
                      </div>
                    </details>
                  )}

                  {/* Pipeline-specific settings */}
                  {voiceConversationSettings.engine === 'pipeline' && !isEdgeLocal && (
                    <>
                      {/* STT Language */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                          Speech Recognition Language
                        </label>
                        <STTLanguagePicker
                          value={voiceConversationSettings.sttLanguage}
                          onChange={(code) =>
                            setVoiceConversation({ sttLanguage: code ?? 'multi' })
                          }
                        />
                        <p className="text-xs text-muted-foreground">
                          Auto-detect supports English, Spanish, French, German, Hindi, Russian,
                          Portuguese, Japanese, Italian, and Dutch in a single conversation. For
                          other languages, pick the language explicitly. All conversations use
                          Deepgram Nova-3.
                        </p>
                      </div>
                    </>
                  )}

                  {/* Realtime-specific settings */}
                  {voiceConversationSettings.engine === 'openai-realtime' && hasOpenAIKey && (
                    <>
                      {/* Realtime Model */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                          Realtime Model
                        </label>
                        <select
                          value={voiceConversationSettings.models.realtimeModel}
                          onChange={(e) =>
                            setVoiceConversation({
                              models: { ...voiceConversationSettings.models, realtimeModel: e.target.value as 'gpt-realtime' | 'gpt-realtime-mini' },
                            })
                          }
                          className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
                        >
                          <option value="gpt-realtime-mini">GPT Realtime Mini (Fast, lower cost)</option>
                          <option value="gpt-realtime">GPT Realtime (Higher quality)</option>
                        </select>
                        <p className="text-xs text-muted-foreground">
                          Mini is recommended for most use cases. Full model provides higher quality at higher cost.
                        </p>
                      </div>

                      {/* Voice Selection */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                          Voice
                        </label>
                        <select
                          value={voiceConversationSettings.realtimeSettings.voice}
                          onChange={(e) =>
                            setVoiceConversation({
                              realtimeSettings: { ...voiceConversationSettings.realtimeSettings, voice: e.target.value as any },
                            })
                          }
                          className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
                        >
                          <option value="alloy">Alloy</option>
                          <option value="ash">Ash</option>
                          <option value="ballad">Ballad</option>
                          <option value="coral">Coral</option>
                          <option value="echo">Echo</option>
                          <option value="sage">Sage</option>
                          <option value="shimmer">Shimmer</option>
                          <option value="verse">Verse</option>
                        </select>
                      </div>

                      {/* Turn Detection Mode */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                          Turn Detection
                        </label>
                        <select
                          value={voiceConversationSettings.realtimeSettings.turnDetection}
                          onChange={(e) =>
                            setVoiceConversation({
                              realtimeSettings: { ...voiceConversationSettings.realtimeSettings, turnDetection: e.target.value as 'server_vad' | 'semantic_vad' },
                            })
                          }
                          className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
                        >
                          <option value="server_vad">Server VAD (Silence-based)</option>
                          <option value="semantic_vad">Semantic VAD (Intent-based)</option>
                        </select>
                        <p className="text-xs text-muted-foreground">
                          Server VAD detects silence to end turns. Semantic VAD uses meaning to detect natural turn boundaries (open-mic only).
                        </p>
                      </div>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Legacy TTS Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mic className="w-5 h-5" />
                Standard TTS Mode
              </CardTitle>
              <CardDescription>
                When streaming TTS is disabled, the standard mode waits for the complete
                response before generating and playing audio.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertTitle>When to use Standard Mode</AlertTitle>
                <AlertDescription>
                  Standard mode may be preferred for slower connections or when you want
                  consistent audio quality without any potential for audio gaps.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>}

        {/* API Keys Tab */}
        <TabsContent value="api-keys" className="space-y-4 mt-4">
          <Alert icon={<Key className="w-5 h-5" />}>
            <AlertTitle>Secure Backend Storage</AlertTitle>
            <AlertDescription>
              API keys are encrypted and stored securely in the backend database.
              Keys are never stored in your browser.
            </AlertDescription>
          </Alert>

          {schemasError ? (
            <Alert variant="destructive">
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>
                  Couldn't load the service catalog
                  {schemasError instanceof Error ? ` — ${schemasError.message}` : ''}
                  . Your keys are unaffected.
                </span>
                <Button variant="ghost" size="sm" onClick={() => refetchSchemas()}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : schemasLoading ? (
            <div className="space-y-4" aria-label="Loading services">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-28 rounded-lg bg-muted animate-pulse" />
              ))}
            </div>
          ) : (
            apiKeyCardSchemas.map((schema) => (
              <ApiKeyCard
                key={schema.service}
                schema={schema}
                description={API_KEY_CARD_UI[schema.service]?.description}
                hasKey={hasKeyByService[schema.service] ?? false}
                managed={isCredentialManaged(schema.service)}
                managedBanner={renderManagedBanner(schema.service)}
                badge={renderCredentialBadge(schema.service, hasKeyByService[schema.service] ?? false)}
                saving={savingCredential === schema.service}
                clearing={savingCredential === `${schema.service}-clear`}
                onSave={(key, config) => handleSaveApiKey(schema, key, config)}
                onClear={() => handleClearApiKey(schema)}
              />
            ))
          )}
          {/* Meta Marketing card: not a registry service — keep verbatim, now after the loop */}
          {showMarketing && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    Meta Marketing
                  </div>
                  {isLoadingMeta ? (
                    <Spinner size="sm" />
                  ) : metaAccount ? (
                    <Badge variant="success">
                      <Check className="w-3 h-3 mr-1" />
                      Connected
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Not connected</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Connect your Meta Ad Account to manage Instagram and Facebook campaigns
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {metaAccount ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-foreground">{metaAccount.name}</p>
                        <p className="text-xs text-muted-foreground">{metaAccount.adAccountId}</p>
                      </div>
                      <Button variant="destructive" size="sm" onClick={disconnectMetaAccount}>
                        <Trash2 className="w-4 h-4 mr-1" />
                        Disconnect
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-foreground">
                        System User Access Token
                      </label>
                      <div className="relative">
                        <Input
                          type={showApiKeys['meta'] ? 'text' : 'password'}
                          placeholder="Enter your Meta System User token"
                          value={metaTokenInput}
                          onChange={(e) => setMetaTokenInput(e.target.value)}
                        />
                        <button
                          onClick={() => toggleApiKeyVisibility('meta')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showApiKeys['meta'] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-foreground">
                        Ad Account ID
                      </label>
                      <Input
                        placeholder="act_123456789"
                        value={metaAdAccountInput}
                        onChange={(e) => setMetaAdAccountInput(e.target.value)}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-foreground">
                          Pixel / Dataset ID <span className="text-muted-foreground font-normal">(optional)</span>
                        </label>
                        <Input
                          placeholder="For Conversions API"
                          value={metaPixelInput}
                          onChange={(e) => setMetaPixelInput(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-foreground">
                          Facebook Page ID <span className="text-muted-foreground font-normal">(optional)</span>
                        </label>
                        <Input
                          placeholder="Linked to WhatsApp"
                          value={metaPageInput}
                          onChange={(e) => setMetaPageInput(e.target.value)}
                        />
                      </div>
                    </div>
                    <Button
                      onClick={connectMetaAccount}
                      disabled={!metaTokenInput.trim() || !metaAdAccountInput.trim() || isConnectingMeta}
                      isLoading={isConnectingMeta}
                      className="w-full"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Connect Meta Ad Account
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Storage Tab */}
        <TabsContent value="storage" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>API Credentials</CardTitle>
              <CardDescription>
                Encrypted credentials stored in the backend database
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(() => {
                const hasAnyKey = hasElevenLabsKey || hasTavusKey || hasAzureKey || hasRagApiKey || hasDeepgramKey || hasOpenAIKey
                const hasUserManagedKey = [
                  CREDENTIAL_SERVICES.ELEVENLABS,
                  CREDENTIAL_SERVICES.TAVUS,
                  CREDENTIAL_SERVICES.AZURE_SPEECH,
                  CREDENTIAL_SERVICES.RAG,
                  CREDENTIAL_SERVICES.DEEPGRAM,
                  CREDENTIAL_SERVICES.OPENAI,
                ].some((svc) => !isCredentialManaged(svc) && (
                  (svc === 'elevenlabs' && hasElevenLabsKey) ||
                  (svc === 'tavus' && hasTavusKey) ||
                  (svc === 'azure-speech' && hasAzureKey) ||
                  (svc === 'rag' && hasRagApiKey) ||
                  (svc === 'deepgram' && hasDeepgramKey) ||
                  (svc === 'openai' && hasOpenAIKey)
                ))
                const managedCount = Object.keys(credentialMeta).filter(
                  (svc) => isCredentialManaged(svc),
                ).length

                return (
                  <>
                    <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                      <div>
                        <h4 className="font-medium">Your API Credentials</h4>
                        <p className="text-sm text-muted-foreground">
                          {hasAnyKey
                            ? 'Some credentials are stored'
                            : 'No credentials stored'}
                        </p>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleClearAllCredentials}
                        disabled={!hasUserManagedKey || savingCredential === 'all-clear'}
                        isLoading={savingCredential === 'all-clear'}
                      >
                        Clear All
                      </Button>
                    </div>
                    {managedCount > 0 && (
                      <div className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground">
                        <Shield className="w-4 h-4 shrink-0" />
                        <span>
                          {managedCount} credential{managedCount > 1 ? 's are' : ' is'} managed by your administrator and cannot be cleared.
                        </span>
                      </div>
                    )}
                  </>
                )
              })()}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Backend Storage</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchStorageStats}
                  disabled={isLoadingStats}
                >
                  {isLoadingStats ? (
                    <Spinner size="sm" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                </Button>
              </CardTitle>
              <CardDescription>
                Data stored in the backend database
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingStats && !storageStats ? (
                <div className="flex items-center justify-center py-4">
                  <Spinner size="lg" />
                </div>
              ) : storageStats ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Personas</span>
                    <Badge variant="secondary">{storageStats.personas_count}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Conversations</span>
                    <Badge variant="secondary">{storageStats.conversations_count}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Messages</span>
                    <Badge variant="secondary">{storageStats.messages_count}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Feedback</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="success" className="flex items-center gap-1">
                        <ThumbsUp className="w-3 h-3" />
                        {storageStats.positive_feedback_count}
                      </Badge>
                      <Badge variant="destructive" className="flex items-center gap-1">
                        <ThumbsDown className="w-3 h-3" />
                        {storageStats.negative_feedback_count}
                      </Badge>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  Unable to fetch storage stats
                </div>
              )}
            </CardContent>
          </Card>

          {/* Linked Accounts — only when inbox feature is enabled */}
          {showInbox && <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Link2 className="w-5 h-5" />
                  Linked Accounts
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadLinkedAccounts}
                  disabled={isLoadingAccounts}
                >
                  {isLoadingAccounts ? (
                    <Spinner size="sm" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                </Button>
              </CardTitle>
              <CardDescription>
                Link your WhatsApp or other external messaging accounts to your web identity
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Linked account list */}
              {linkedAccounts.length > 0 ? (
                <div className="space-y-2">
                  {linkedAccounts.map((account) => (
                    <div
                      key={account.id}
                      className="flex items-center justify-between p-3 bg-muted rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {account.channel === 'whatsapp' ? (
                          <Phone className="w-4 h-4 text-green-500" />
                        ) : (
                          <Globe className="w-4 h-4 text-blue-500" />
                        )}
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {account.displayName || account.channelUserId}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {account.channel === 'whatsapp' ? 'WhatsApp' : account.channel}
                            {account.linkedAt && (
                              <> &middot; Linked {new Date(account.linkedAt).toLocaleDateString()}</>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {account.isVerified && (
                          <Badge variant="success" className="text-xs">
                            <Check className="w-3 h-3 mr-0.5" />
                            Verified
                          </Badge>
                        )}
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleUnlinkAccount(account.id)}
                          disabled={unlinkingId === account.id}
                          isLoading={unlinkingId === account.id}
                        >
                          Unlink
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-2">
                  No linked accounts
                </p>
              )}

              {/* Generate link code */}
              <div className="border-t border-border pt-4">
                <h4 className="text-sm font-medium text-foreground mb-2">
                  Link a WhatsApp Account
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Generate a code, then send it as a message to your AI persona on WhatsApp to link your accounts.
                </p>
                {linkCode ? (
                  <div className="flex items-center gap-3 p-3 bg-primary/10 rounded-lg">
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground mb-1">Your link code:</p>
                      <p className="text-2xl font-mono font-bold tracking-widest text-primary">
                        {linkCode}
                      </p>
                      {linkCodeExpiry && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Expires {new Date(linkCodeExpiry).toLocaleTimeString()}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button variant="secondary" size="sm" onClick={copyLinkCode}>
                        <Copy className="w-3.5 h-3.5 mr-1" />
                        Copy
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleGenerateLinkCode}
                        disabled={isGeneratingCode}
                        isLoading={isGeneratingCode}
                      >
                        New Code
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={handleGenerateLinkCode}
                    disabled={isGeneratingCode}
                    isLoading={isGeneratingCode}
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Generate Link Code
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>}

          {/* Local Feedback Stats */}
          {localFeedbackStats.totalMessages > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Local Feedback Queue</CardTitle>
                <CardDescription>
                  Feedback awaiting sync to backend
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Pending Sync</span>
                    <Badge variant={pendingSyncCount > 0 ? 'warning' : 'success'}>
                      {pendingSyncCount} items
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Total Local</span>
                    <Badge variant="secondary">{localFeedbackStats.totalMessages}</Badge>
                  </div>
                  {localFeedbackStats.totalMessages > 0 && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        clearLocalFeedback()
                        addToast({
                          title: 'Local feedback cleared',
                          description: 'All locally stored feedback has been removed.',
                          type: 'success',
                        })
                      }}
                      className="w-full mt-2"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear Local Feedback
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </PageFrame>
  )
}
